import { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { useDocumentHead } from '@/hooks/use-document-head'
import { SettingsPageLayout } from '@/components/layout/SettingsPageLayout'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Contact, Building2, Upload } from 'lucide-react'
import { ContactCSVUploadDialog } from '@/components/csv-upload/ContactCSVUploadDialog'
import { PropertyCSVUploadDialog } from '@/components/csv-upload/PropertyCSVUploadDialog'
import { cn } from '@/lib/utils'
import {
  RelationshipAccessLevel,
  type ContactWithRelationships,
  type Property,
  formatUSPhoneForDisplay,
} from '@super/shared-types'
import { getContactDisplayName } from '@/lib/utils/contact-display'
import type { CSVProcessingResult } from '@/lib/csv-upload/contact-csv-processor'
import { contactsApiClient, propertiesApiClient } from '@/lib/api'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PropertyGroupsTab } from '@/components/settings/property-groups/PropertyGroupsTab'
import { toast } from 'sonner'
import { DataTable } from '@/components/directory/DataTable'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Heading, Text } from '@/components/ui/typography'

type TabValue = 'contacts' | 'properties' | 'property-groups'

export function DirectorySettingsPage() {
  useDocumentHead({
    title: 'Directory Settings',
    description: 'Manage your contacts and properties',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [activeTab, setActiveTab] = useState<TabValue>('properties')
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  // Bumped by the header "Add group" button; PropertyGroupsTab opens its
  // create dialog on change.
  const [createGroupSignal, setCreateGroupSignal] = useState(0)
  const [preselectedCsvType, setPreselectedCsvType] = useState<
    'properties' | 'units'
  >('properties')
  const [existingContacts, setExistingContacts] = useState<
    ContactWithRelationships[]
  >([])
  const [isLoadingContacts, setIsLoadingContacts] = useState(true)
  const [existingProperties, setExistingProperties] = useState<Property[]>([])
  const [isLoadingProperties, setIsLoadingProperties] = useState(true)
  const [preloadedFile, setPreloadedFile] = useState<File | null>(null)
  const [preselectedRelationship, setPreselectedRelationship] = useState<
    RelationshipAccessLevel | ''
  >('')

  // Fetch existing contacts and properties on component mount
  useEffect(() => {
    if (currentCompany?.id) {
      fetchExistingContacts()
      fetchExistingProperties()
    }
  }, [currentCompany?.id])

  const fetchExistingContacts = async () => {
    if (!currentCompany?.id) return

    setIsLoadingContacts(true)
    try {
      const contacts = isSuperAdmin
        ? await contactsApiClient.getAllAdminContactsForCompany(
            currentCompany.id,
          )
        : await contactsApiClient.listForAuthedCompany()
      setExistingContacts(contacts)
      console.log(`Loaded ${contacts.length} existing contacts`)
    } catch (error) {
      console.error('Failed to fetch existing contacts:', error)
      toast.error('Failed to load existing contacts')
    } finally {
      setIsLoadingContacts(false)
    }
  }

  const fetchExistingProperties = async () => {
    if (!currentCompany?.id) return

    setIsLoadingProperties(true)
    try {
      const properties = isSuperAdmin
        ? await propertiesApiClient.getAllAdminPropertiesForCompany(
            currentCompany.id,
          )
        : await propertiesApiClient.getProperties()
      setExistingProperties(properties)
      console.log(`Loaded ${properties.length} existing properties`)
    } catch (error) {
      console.error('Failed to fetch existing properties:', error)
      toast.error('Failed to load existing properties')
    } finally {
      setIsLoadingProperties(false)
    }
  }

  const handleUpload = () => {
    setShowUploadDialog(true)
  }

  const detectRelationshipFromFilename = (
    filename: string,
  ): RelationshipAccessLevel | '' => {
    const lowercaseFilename = filename.toLowerCase()
    if (lowercaseFilename.includes('tenant'))
      return RelationshipAccessLevel.TENANT
    if (lowercaseFilename.includes('owner'))
      return RelationshipAccessLevel.OWNER
    if (lowercaseFilename.includes('vendor'))
      return RelationshipAccessLevel.VENDOR
    if (lowercaseFilename.includes('manager'))
      return RelationshipAccessLevel.MANAGER
    return ''
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        const lowercaseName = file.name.toLowerCase()

        setPreloadedFile(file)

        if (lowercaseName.includes('unit')) {
          setActiveTab('properties')
          setPreselectedCsvType('units')
          setShowUploadDialog(true)
          return
        }

        setPreselectedCsvType('properties')

        // Auto-detect relationship type for contacts from filename
        if (activeTab === 'contacts') {
          const detectedRelationship = detectRelationshipFromFilename(file.name)
          setPreselectedRelationship(detectedRelationship)
        }

        setShowUploadDialog(true)
      }
    },
    [activeTab],
  )

  const handleContactUploadComplete = (_result: CSVProcessingResult) => {
    setPreloadedFile(null)
    setPreselectedRelationship('')
    fetchExistingContacts()
  }

  const handlePropertyOrUnitUploadComplete = () => {
    setPreloadedFile(null)
    fetchExistingProperties()
  }

  const handleDialogOpenChange = (open: boolean) => {
    setShowUploadDialog(open)
    if (!open) {
      setPreloadedFile(null)
      setPreselectedRelationship('')
      setPreselectedCsvType('properties')
    }
  }

  const EmptyState = ({ type }: { type: 'contacts' | 'properties' }) => {
    const {
      getRootProps: getDropzoneProps,
      getInputProps,
      isDragActive,
    } = useDropzone({
      onDrop,
      accept: {
        'text/csv': ['.csv'],
      },
      maxFiles: 1,
      noClick: true,
    })

    return (
      <div {...getDropzoneProps()} className="relative overflow-hidden">
        <motion.div
          className={cn(
            'relative flex flex-col items-center justify-center py-12 px-4 text-center border-2 border-dashed rounded-lg transition-all duration-300 ease-out min-h-96',
            isDragActive
              ? 'border-success bg-success-subtle shadow-lg'
              : 'border-border',
          )}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          <input {...getInputProps()} />

          <AnimatePresence>
            {isDragActive && (
              <>
                {/* Background blur layer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="absolute inset-0 rounded-lg"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                />

                {/* Content layer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="absolute inset-0 flex items-center justify-center rounded-lg"
                  style={{
                    pointerEvents: 'none',
                    zIndex: 20,
                  }}
                >
                  <motion.div
                    initial={{ y: 20, opacity: 0, scale: 0.8 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -20, opacity: 0, scale: 0.8 }}
                    transition={{
                      type: 'spring',
                      stiffness: 600,
                      damping: 30,
                      delay: 0.1,
                    }}
                    className="bg-background p-6 rounded-xl shadow-xl border border-success-border"
                  >
                    <motion.div
                      initial={{ y: -5, rotate: -5 }}
                      animate={{ y: 0, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                        delay: 0.2,
                      }}
                    >
                      <Upload className="h-8 w-8 text-success-dim mx-auto mb-3" />
                    </motion.div>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-base font-semibold text-success-emphasis"
                    >
                      Drop CSV file to upload {type}
                    </motion.p>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <motion.div
            animate={
              isDragActive
                ? { scale: 0.9, opacity: 0.3 }
                : { scale: 1, opacity: 1 }
            }
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center"
          >
            {type === 'contacts' ? (
              <Contact className="h-8 w-8 text-muted-foreground mb-4" />
            ) : (
              <Building2 className="h-8 w-8 text-muted-foreground mb-4" />
            )}
            <Heading variant="subsection-title" className="mb-2">
              No {type} found
            </Heading>
            <Text variant="muted" className="mb-4">
              Drag & drop a CSV file here or click the button below to upload
              your {type}
            </Text>
            <Button
              onClick={e => {
                e.stopPropagation()
                handleUpload()
              }}
              variant="default"
              className="pointer-events-auto"
            >
              Upload CSV
            </Button>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  const ContactsTab = () => {
    const {
      getRootProps: getContactsDropzoneProps,
      getInputProps: getContactsInputProps,
      isDragActive: isContactsDragActive,
    } = useDropzone({
      onDrop,
      accept: {
        'text/csv': ['.csv'],
      },
      maxFiles: 1,
      noClick: true,
    })

    if (isLoadingContacts) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="rounded-lg border overflow-hidden">
            <div className="bg-muted px-6 py-3 flex space-x-4">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="divide-y divide-border">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="px-6 py-4 flex space-x-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }

    if (existingContacts.length === 0) {
      return <EmptyState type="contacts" />
    }

    const contactColumns = [
      {
        header: 'Name',
        cell: (contact: ContactWithRelationships) => (
          <span className="font-medium text-foreground">
            {getContactDisplayName(contact)}
          </span>
        ),
      },
      {
        header: 'Email',
        cell: (contact: ContactWithRelationships) =>
          contact.primaryEmail || '-',
      },
      {
        header: 'Phone',
        cell: (contact: ContactWithRelationships) =>
          contact.primaryPhoneNumber
            ? formatUSPhoneForDisplay(contact.primaryPhoneNumber)
            : '-',
      },
      {
        header: 'Relationships',
        cell: (contact: ContactWithRelationships) => (
          <span>
            {contact.relationships.length}{' '}
            {contact.relationships.length === 1 ? 'property' : 'properties'}
          </span>
        ),
      },
    ]

    return (
      <>
        <DataTable
          data={existingContacts}
          columns={contactColumns}
          itemType="contacts"
          isDragActive={isContactsDragActive}
          dropzoneProps={getContactsDropzoneProps()}
          inputProps={getContactsInputProps()}
        />
      </>
    )
  }

  const PropertiesTab = () => {
    const {
      getRootProps: getPropertiesDropzoneProps,
      getInputProps: getPropertiesInputProps,
      isDragActive: isPropertiesDragActive,
    } = useDropzone({
      onDrop,
      accept: {
        'text/csv': ['.csv'],
      },
      maxFiles: 1,
      noClick: true,
    })

    if (isLoadingProperties) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="rounded-lg border overflow-hidden">
            <div className="bg-muted px-6 py-3 flex space-x-4">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="p-6">
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      )
    }

    if (existingProperties.length === 0) {
      return <EmptyState type="properties" />
    }

    const propertyColumns = [
      {
        header: 'Name',
        cell: (property: any) => (
          <span className="font-medium text-foreground">
            {property.propertyName || property.name || '-'}
          </span>
        ),
      },
      {
        header: 'Address',
        width: '300px',
        className: 'max-w-0',
        cell: (property: any) => {
          if (!property.propertyAddress) return '-'
          const fullAddress = `${property.propertyAddress.addressStreet1}${property.propertyAddress.addressStreet2 ? `, ${property.propertyAddress.addressStreet2}` : ''}, ${property.propertyAddress.addressCity}, ${property.propertyAddress.addressState} ${property.propertyAddress.addressPostcode}`
          return (
            <div title={fullAddress} className="truncate">
              {fullAddress}
            </div>
          )
        },
      },
      {
        header: 'Type',
        cell: (property: any) =>
          Array.isArray(property.propertyType)
            ? property.propertyType.join(', ')
            : property.propertyType || '-',
      },
      {
        header: 'External ID',
        cell: (property: any) => {
          const id = property.externalId
          // AppFolio exports without a Property ID column get a composite key (name + address) — too long to display; only show numeric PMS IDs
          return id && /^\d+$/.test(id) ? id : '-'
        },
      },
    ]

    return (
      <div className="pb-20">
        <DataTable
          data={existingProperties}
          columns={propertyColumns}
          itemType="properties"
          isDragActive={isPropertiesDragActive}
          dropzoneProps={getPropertiesDropzoneProps()}
          inputProps={getPropertiesInputProps()}
        />
      </div>
    )
  }

  const tabs = [
    {
      value: 'properties',
      label: 'Properties',
      buttonText: 'Upload CSV',
      content: <PropertiesTab />,
    },
    {
      value: 'contacts',
      label: 'Contacts',
      buttonText: 'Upload CSV',
      content: <ContactsTab />,
    },
    {
      value: 'property-groups',
      label: 'Property Groups',
      buttonText: 'Add group',
      content: (
        <PropertyGroupsTab
          properties={existingProperties}
          isLoadingProperties={isLoadingProperties}
          createSignal={createGroupSignal}
        />
      ),
    },
  ]

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div>
          <SettingsPageLayout
            title="Manage your directory properties and contacts"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={value => setActiveTab(value as TabValue)}
            onMainAction={
              activeTab === 'property-groups'
                ? () => setCreateGroupSignal(n => n + 1)
                : handleUpload
            }
          />

          {activeTab === 'contacts' ? (
            <ContactCSVUploadDialog
              open={showUploadDialog}
              onOpenChange={handleDialogOpenChange}
              onUploadComplete={handleContactUploadComplete}
              preloadedFile={preloadedFile}
              preselectedRelationship={preselectedRelationship}
              existingContacts={existingContacts}
              onFileChange={setPreloadedFile}
            />
          ) : (
            <PropertyCSVUploadDialog
              open={showUploadDialog}
              onOpenChange={handleDialogOpenChange}
              onUploadComplete={handlePropertyOrUnitUploadComplete}
              preloadedFile={preloadedFile}
              onFileChange={setPreloadedFile}
              existingProperties={existingProperties}
              preselectedCsvType={preselectedCsvType}
            />
          )}

          {/* Floating Action Island */}
          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>
    </div>
  )
}
